// @ts-nocheck
import {
  backButton,
  viewport,
  miniApp,
  swipeBehavior,
  locationManager,
  init,
  initData,
  on,
  retrieveLaunchParams,
} from "@telegram-apps/sdk";
import { calculateOffset } from "@shared/integrations/telegram/safeArea";
import { noop } from "@shared/utils/noop";
import { throttle } from "@shared/utils/debounce";

/**
 * Получает start_param из Telegram Mini App
 * Используется для deep linking (например, ?startapp=cards)
 */
export const getStartParam = (): string | null => {
  try {
    console.log("[getStartParam] Checking for start_param...");
    
    // Способ 1: через window.Telegram.WebApp (самый надежный)
    if (typeof window !== 'undefined' && (window as any).Telegram?.WebApp?.initDataUnsafe?.start_param) {
      const param = (window as any).Telegram.WebApp.initDataUnsafe.start_param;
      console.log("[getStartParam] Got from window.Telegram.WebApp:", param);
      return param;
    }
    
    // Способ 2: через @telegram-apps/sdk
    try {
      const launchParams = retrieveLaunchParams();
      console.log("[getStartParam] SDK launchParams:", JSON.stringify(launchParams));
      if (launchParams?.startParam) {
        console.log("[getStartParam] Got from SDK:", launchParams.startParam);
        return launchParams.startParam;
      }
    } catch (sdkError) {
      console.warn("[getStartParam] SDK retrieveLaunchParams failed:", sdkError);
    }
    
    // Способ 3: парсинг из URL (для случаев когда tgWebAppStartParam в hash)
    if (typeof window !== 'undefined') {
      const hash = window.location.hash;
      const search = window.location.search;
      const fullUrl = hash + search;
      
      // Ищем tgWebAppStartParam в URL
      const match = fullUrl.match(/tgWebAppStartParam=([^&]+)/);
      if (match) {
        const param = decodeURIComponent(match[1]);
        console.log("[getStartParam] Got from URL parsing:", param);
        return param;
      }
    }
    
    console.log("[getStartParam] No start_param found");
    return null;
  } catch (error) {
    console.warn("[getStartParam] Error getting start_param:", error);
    return null;
  }
};

/**
 * Закрывает Telegram Mini App
 * Используется перед переходом на внешние страницы (например, T-Bank)
 */
export const closeMiniApp = (): void => {
  console.log("[closeMiniApp] Attempting to close Mini App...");
  
  try {
    // Способ 1: через window.Telegram.WebApp (самый надёжный)
    if (typeof window !== 'undefined' && (window as any).Telegram?.WebApp?.close) {
      console.log("[closeMiniApp] Using window.Telegram.WebApp.close()");
      (window as any).Telegram.WebApp.close();
      return;
    }
    
    // Способ 2: через @telegram-apps/sdk
    try {
      console.log("[closeMiniApp] Checking SDK miniApp.close.isAvailable():", miniApp.close.isAvailable());
      if (miniApp.close.isAvailable()) {
        console.log("[closeMiniApp] Using SDK miniApp.close()");
        miniApp.close();
        return;
      }
    } catch (sdkError) {
      console.warn("[closeMiniApp] SDK close failed:", sdkError);
    }
    
    console.warn("[closeMiniApp] Close method not available");
  } catch (error) {
    console.error("[closeMiniApp] Error closing Mini App:", error);
  }
};

/**
 * Закрывает Mini App с задержкой (для случаев когда нужно дождаться открытия ссылки)
 */
export const closeMiniAppDelayed = (delayMs: number = 500): void => {
  console.log(`[closeMiniAppDelayed] Will close Mini App in ${delayMs}ms`);
  setTimeout(() => {
    closeMiniApp();
  }, delayMs);
};

function isMobileUA(): boolean {
  const ua = navigator.userAgent.toLowerCase();
  return /android|iphone|ipad|ipod/i.test(ua);
}

export const initTelegram = (): string | null => {
  try {
    init();
  } catch (error) {
    // init() fails when app is opened outside Telegram WebView
    // This is expected in development - return null to use anonymous mode
    return null;
  }

  [
    viewport.mount,
    miniApp.mountSync,
    miniApp.bindCssVars,
    viewport.bindCssVars,
    locationManager.mount,
    swipeBehavior.mount,
    swipeBehavior.disableVertical,
  ].forEach((fn) => fn.ifAvailable());

  // Блокируем ориентацию экрана через Telegram WebApp API (Bot API 8.0+)
  if (typeof window !== 'undefined' && (window as any).Telegram?.WebApp) {
    const tgWebApp = (window as any).Telegram.WebApp;
    
    // lockOrientation() фиксирует ориентацию Mini App на текущем режиме
    // Вызываем после mount, чтобы убедиться, что API готово
    if (tgWebApp.lockOrientation && typeof tgWebApp.lockOrientation === 'function') {
      tgWebApp.lockOrientation();
    }
  }

  if (viewport.requestFullscreen.isAvailable() && isMobileUA()) {
    viewport.requestFullscreen().catch(noop);
  }

  backButton.onClick.ifAvailable(() => {
    miniApp.close.ifAvailable();
    window.close();
  });

  try {
    initData.restore();
  } catch (error) {
    // initData.restore() fails when app is opened outside Telegram WebView
    // This is expected in development - return null to use anonymous mode
    return null;
  }

  const initDataRaw = initData.raw();

  if (!initDataRaw) {
    console.error("❌ initData.raw() returned null or undefined");
    return null;
  }

  // Вызываем сразу для установки начального значения
  calculateOffset();

  setTimeout(() => {
    if (isMobileUA() && viewport.requestFullscreen.isAvailable()) {
      viewport.requestFullscreen().catch(noop);
    }
    setTimeout(() => {
      calculateOffset();
    }, 500);

    // Throttle viewport_changed events to reduce spam (max once per 100ms)
    const throttledCalculateOffset = throttle(calculateOffset, 100);
    on("viewport_changed", throttledCalculateOffset);
  }, 500);

  return "tma " + initDataRaw;
};

/**
 * Получает свежий initDataRaw из Telegram WebApp
 * Используется для получения актуального токена при каждом запросе
 * Вызывает restore() для обновления данных из Telegram перед получением токена
 */
export const getFreshInitDataRaw = (): string | null => {
  try {
    console.log("[getFreshInitDataRaw] Attempting to get fresh initData...");
    // Обновляем данные из Telegram перед получением токена
    try {
      initData.restore();
      console.log("[getFreshInitDataRaw] initData.restore() succeeded");
    } catch (restoreError) {
      // restore() может не работать вне Telegram WebView - это нормально
      console.warn("[getFreshInitDataRaw] initData.restore() failed (expected outside Telegram):", restoreError);
    }
    
    const rawData = initData.raw();
    console.log(`[getFreshInitDataRaw] initData.raw() returned: ${rawData ? `yes (length: ${rawData.length})` : 'null'}`);
    if (!rawData) {
      console.warn("[getFreshInitDataRaw] initData.raw() returned null");
      return null;
    }
    
    const result = "tma " + rawData;
    console.log(`[getFreshInitDataRaw] Returning token with length: ${result.length}`);
    return result;
  } catch (error) {
    console.error("[getFreshInitDataRaw] Error getting fresh initData:", error);
    return null;
  }
};

/**
 * Разрешение бота писать пользователю.
 *
 * Приложение открывается ссылкой `?startapp`, минуя диалог с ботом: `/start` бот не
 * получает, переписки нет, и отправить сообщение он не может. Этот метод спрашивает
 * разрешение — после согласия бот пишет пользователю, например когда заказ готов.
 *
 * Спрашиваем один раз: отметка о согласии или отказе хранится локально. Повторно
 * дёргать окно на каждом заказе — раздражать без пользы.
 */
const WRITE_ACCESS_KEY = "tg_write_access_asked";

export const hasAskedWriteAccess = (): boolean => {
  try {
    return localStorage.getItem(WRITE_ACCESS_KEY) !== null;
  } catch {
    return false;
  }
};

export const requestWriteAccess = (): Promise<boolean> => {
  return new Promise((resolve) => {
    const webApp = typeof window !== "undefined" ? (window as any).Telegram?.WebApp : null;

    if (!webApp || typeof webApp.requestWriteAccess !== "function") {
      // Метод появился в Bot API 6.9; вне Telegram или в старом клиенте его нет.
      console.warn("[requestWriteAccess] Метод недоступен в этом клиенте");
      resolve(false);
      return;
    }

    try {
      webApp.requestWriteAccess((granted: boolean) => {
        try {
          localStorage.setItem(WRITE_ACCESS_KEY, granted ? "granted" : "denied");
        } catch {
          // Приватный режим — просто спросим ещё раз в следующий раз.
        }
        console.log(`[requestWriteAccess] Ответ пользователя: ${granted ? "разрешил" : "отказал"}`);
        resolve(Boolean(granted));
      });
    } catch (error) {
      console.error("[requestWriteAccess] Ошибка запроса:", error);
      resolve(false);
    }
  });
};
